/**
 * Sticky Add to Cart Component
 * 
 * Shows a sticky add to cart bar when the regular add to cart button goes out of view.
 * Automatically syncs with the main product form and variant selection.
 */

// Add CSS for screen reader only class
const style = document.createElement('style');
style.textContent = `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
document.head.appendChild(style);

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.productFormComponent = null;
    this.intersectionObserver = null;
    this.isVisible = false;
    this.shouldBeVisible = false;
    this.addToCartButton = null;
  }

  init() {
    // Find the regular add to cart button
    this.addToCartButton = this.querySelector('button[name="add"]');
    this.shopNowButton = this.querySelector('.sticky-add-to-cart__shop-now-button');
    this.shopNowContainer = this.querySelector('.sticky-add-to-cart__content');
    if (!(this.addToCartButton instanceof HTMLButtonElement)) throw new Error('Add to cart button not found');
    this.productFormComponent = document.querySelector(`form#${this.addToCartButton.getAttribute('form')}`);
    if (!this.productFormComponent) throw new Error('Product form component not found');

    // Option selectors
    this.selectOptionSelectors = document.querySelectorAll('.variant-picker select.variant-option__select');
    this.thisOptionSelectors = this.querySelectorAll('select.variant-option__select');
    
    if (this.productFormComponent) {
      this.setupIntersectionObserver();
      this.setupEventListeners();
      this.syncVariantSelection();
      this.shopNowHandle();
      this.optionSelectorHandle();
    }
  }

  connectedCallback() {
    this.init();
  }

  setupIntersectionObserver() {
    if (!this.productFormComponent) return;
    const variantPickerForm = document.querySelector('.variant-picker__form');
    let variantPickerIntersection = false;
    let productFormIntersection = false;

    // Create intersection observer to detect when add to cart goes out of view
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.target === this.productFormComponent) {
            productFormIntersection = !entry.isIntersecting;
          }
          if (entry.target === variantPickerForm) {
            variantPickerIntersection = !entry.isIntersecting;
          }
          // Handles the edge case where there is no variant picker form
          if(!variantPickerForm) variantPickerIntersection = true;
          if (productFormIntersection && variantPickerIntersection) {
            this.shouldBeVisible = true;
          } else {
            this.shouldBeVisible = false;
          }
          this.handleVisibilityChange();
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9,1]
      }
    );

    this.intersectionObserver.observe(this.productFormComponent);

    if (variantPickerForm) {
      this.intersectionObserver.observe(variantPickerForm);
    }
  }

  setupEventListeners() {
    // Listen for subscription price updates from the main form
    document.addEventListener('subscription:price-update', (event) => {
      console.log('[Sticky Cart] Received price update event:', event.detail);
      this.handleSubscriptionPriceUpdate(event.detail);
    });
    
    // Listen for variant changes in the main form - enhanced to catch more events
    const variantPickers = document.querySelectorAll('input[name="id"], select[name="id"], select[name^="options["], input[type="radio"][name^="options["]');
    
    variantPickers.forEach((picker) => {
      picker.addEventListener('change', () => {
        setTimeout(() => this.syncVariantSelection(), 50); // Small delay to ensure DOM updates
      });
    });

    // Also listen for theme-specific radio buttons (flavour-blockId-productId pattern)
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const blockId = variantPicker.getAttribute('data-block-id');
      const productId = variantPicker.getAttribute('data-product-id');
      
      if (blockId && productId) {
        // Listen for any radio button that matches the theme naming pattern
        const themeRadioButtons = document.querySelectorAll(`input[type="radio"][name*="-${blockId}-${productId}"]`);
        
        themeRadioButtons.forEach((radio) => {
          radio.addEventListener('change', () => {
            setTimeout(() => this.syncVariantSelection(), 50);
          });
        });
      }
    }

    // Listen for variant-picker specific events (most reliable)
    if (variantPicker) {
      // Listen for the VariantSelectedEvent and VariantUpdateEvent from variant-picker
      variantPicker.addEventListener('variant-selected', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
      
      variantPicker.addEventListener('variant-update', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
      
      // Also listen for direct change events on the variant-picker
      variantPicker.addEventListener('change', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
    }
    
    // Also listen for custom variant events (fallback)
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.syncVariantSelection(), 50);
    });

    // Listen for product form variant updates (gets actual selected variant with correct image)
    document.addEventListener('product-form:variant:update', (event) => {
      // @ts-ignore - Custom event with detail property
      if (event.detail && event.detail.resource) {
        // @ts-ignore - Custom event with detail property
        this.updateImagesFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updateFlavourTextFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updatePricingFromVariant(event.detail.resource);
        // Update button state based on variant availability from the event
        // But verify the form has been updated with the new variant ID first
        // @ts-ignore - Custom event with detail property
        const variant = event.detail.resource;
        if (variant && this.addToCartButton instanceof HTMLButtonElement) {
          const isAvailable = variant.available !== false;
          const eventVariantId = variant.id?.toString();
          
          // Also check sticky dropdown options for sold-out status (more reliable)
          const optionSelectors = this.querySelectorAll('select[name^="options["]');
          let hasUnavailableOption = false;
          let hasSoldOutMetafield = false;
          
          optionSelectors.forEach((selector) => {
            if (!(selector instanceof HTMLSelectElement)) return;
            const selectedOption = selector.options[selector.selectedIndex];
            
            // Check standard availability
            if (selectedOption && (selectedOption.getAttribute('data-variant-available') ?? '').includes('false')) {
              hasUnavailableOption = true;
            }
            
            // Check sold-out metafield
            if (selectedOption && (selectedOption.getAttribute('data-variant-sold-out-metafield') ?? '').includes('true')) {
              hasSoldOutMetafield = true;
            }
          });
          
          const isSoldOut = hasUnavailableOption || hasSoldOutMetafield;
          
          // Wait for form to be fully updated before enabling button
          // Verify the variant ID in the form matches the event variant ID
          setTimeout(() => {
            const variantIdInput = this.productFormComponent?.querySelector('input[name="id"]');
            const formVariantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : null;
            
            // Only enable button if variant is available AND not sold out AND form variant ID matches event variant ID
            if (this.addToCartButton instanceof HTMLButtonElement) {
              const variantMatches = formVariantId === eventVariantId;
              
              if (isAvailable && !isSoldOut && variantMatches) {
                this.addToCartButton.disabled = false;
                this.addToCartButton.dataset.available = 'true';
              } else {
                // Keep disabled if variant unavailable OR sold out OR form not synced yet
                this.addToCartButton.disabled = true;
                this.addToCartButton.dataset.available = 'false';
                
                // Update button text if sold out
                if (isSoldOut) {
                  const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
                  if (buttonText) {
                    buttonText.textContent = this.formatSubscriptionCopy('sold_out', {}, 'Sold Out');
                  }
                  this.addToCartButton.setAttribute('aria-label', this.formatSubscriptionCopy('sold_out', {}, 'Sold out'));
                }
              }
            }
          }, 100); // Small delay to ensure form is fully updated
        }
        // Also sync sticky dropdowns to match main picker
        setTimeout(() => this.syncVariantSelection(), 50);
      }
    });

    // Also listen for the main variant update event
    document.addEventListener('variant:update', (event) => {
      // @ts-ignore - Custom event with detail property
      if (event.detail && event.detail.resource) {
        // @ts-ignore - Custom event with detail property
        this.updateImagesFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updateFlavourTextFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updatePricingFromVariant(event.detail.resource);
        // Update button state based on variant availability from the event
        // But verify the form has been updated with the new variant ID first
        // @ts-ignore - Custom event with detail property
        const variant = event.detail.resource;
        if (variant && this.addToCartButton instanceof HTMLButtonElement) {
          const isAvailable = variant.available !== false;
          const eventVariantId = variant.id?.toString();
          
          // Also check sticky dropdown options for sold-out status (more reliable)
          const optionSelectors = this.querySelectorAll('select[name^="options["]');
          let hasUnavailableOption = false;
          let hasSoldOutMetafield = false;
          
          optionSelectors.forEach((selector) => {
            if (!(selector instanceof HTMLSelectElement)) return;
            const selectedOption = selector.options[selector.selectedIndex];
            
            // Check standard availability
            if (selectedOption && (selectedOption.getAttribute('data-variant-available') ?? '').includes('false')) {
              hasUnavailableOption = true;
            }
            
            // Check sold-out metafield
            if (selectedOption && (selectedOption.getAttribute('data-variant-sold-out-metafield') ?? '').includes('true')) {
              hasSoldOutMetafield = true;
            }
          });
          
          const isSoldOut = hasUnavailableOption || hasSoldOutMetafield;
          
          // Wait for form to be fully updated before enabling button
          // Verify the variant ID in the form matches the event variant ID
          setTimeout(() => {
            const variantIdInput = this.productFormComponent?.querySelector('input[name="id"]');
            const formVariantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : null;
            
            // Only enable button if variant is available AND not sold out AND form variant ID matches event variant ID
            if (this.addToCartButton instanceof HTMLButtonElement) {
              const variantMatches = formVariantId === eventVariantId;
              
              if (isAvailable && !isSoldOut && variantMatches) {
                this.addToCartButton.disabled = false;
                this.addToCartButton.dataset.available = 'true';
              } else {
                // Keep disabled if variant unavailable OR sold out OR form not synced yet
                this.addToCartButton.disabled = true;
                this.addToCartButton.dataset.available = 'false';
                
                // Update button text if sold out
                if (isSoldOut) {
                  const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
                  if (buttonText) {
                    buttonText.textContent = this.formatSubscriptionCopy('sold_out', {}, 'Sold Out');
                  }
                  this.addToCartButton.setAttribute('aria-label', this.formatSubscriptionCopy('sold_out', {}, 'Sold out'));
                }
              }
            }
          }, 100); // Small delay to ensure form is fully updated
        }
        // Also sync sticky dropdowns to match main picker
        setTimeout(() => this.syncVariantSelection(), 50);
      }
    });

    // Listen for variant changes in the sticky bar
    const stickyVariantPickers = this.querySelectorAll('select[data-option-name]');
    
    stickyVariantPickers.forEach((picker) => {
      picker.addEventListener('change', (event) => {
        const target = event.target;
        if(target instanceof HTMLSelectElement && target.value) {
          // Don't call updateStickyVariantImage here - let events handle it to avoid flash
          this.syncMainVariantSelection(target.dataset.optionName ?? '', target.value);
          this.updateSubscriptionPricing();
          // Temporarily disable button while variant is updating
          this.disableAtcButton();
          
          // Update flavour name when sticky option changes
          this.updateSelectedFlavourFromSticky(target);
          
          // Button will be re-enabled by updateButtonState() or variant update events
        }
      });
    });

    // Listen for subscription changes in the sticky bar
    this.setupSubscriptionListeners();

    // Listen for subscription changes in the MAIN form
    this.setupMainSubscriptionListeners();

    // Listen for form submission to hide sticky bar temporarily
    const productForm = document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
      productForm.addEventListener('submit', () => {
        this.hideTemporarily();
      });
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => {
        if (!this.addToCartButton) return;
        this.addToCartButton.classList.add('atc-added');
        setTimeout(() => {
          if (!this.addToCartButton) return;
          this.addToCartButton.classList.remove('atc-added');
        }, 2000);
      });
    }
  }

  enableAtcButton() {
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    this.addToCartButton.disabled = false;
  }
  disableAtcButton() {
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    this.addToCartButton.disabled = true;
  }

  handleVisibilityChange() {
    if (this.shouldBeVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    this.hidden = false;
    this.setAttribute('data-visible', 'true');
    
    // Add body padding to prevent content from being hidden behind sticky bar
    const stickyHeight = this.offsetHeight;
    document.body.style.paddingBottom = `${stickyHeight}px`;
    
    // Sync variants when showing
    this.syncVariantSelection();
    
    // Announce to screen readers
    this.announceToScreenReader('Sticky add to cart bar is now visible');
  }

  hide() {
    this.setAttribute('data-visible', 'false');
    
    // Remove body padding
    document.body.style.paddingBottom = '';
    
    // Hide after animation completes
    setTimeout(() => {
      if (!this.shouldBeVisible) {
        this.hidden = true;
      }
    }, 300);
    
    // Announce to screen readers
    this.announceToScreenReader('Sticky add to cart bar is now hidden');
  }

  hideTemporarily() {
    // Hide sticky bar temporarily during form submission
    this.hide();
    
    // Show again after a delay
    setTimeout(() => {
      this.updateVisibility();
    }, 1000);
  }

  updateVisibility() {
    if(this.shouldBeVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  syncVariantSelection() {
    
    // Get all sticky dropdowns
    const stickySelectors = this.querySelectorAll('select[data-option-name]');
    
    stickySelectors.forEach((stickySelector) => {
      if(!(stickySelector instanceof HTMLSelectElement)) return;
      const optionName = stickySelector.getAttribute('data-option-name');
      
      // Find any checked radio button that matches this option name
      const checkedRadio = document.querySelector(`input[type="radio"]:checked[name*="${optionName}"]`);
      
      if (checkedRadio instanceof HTMLInputElement && checkedRadio.value !== stickySelector.value) {
        const oldValue = stickySelector.value;
        stickySelector.value = checkedRadio.value;
      }
    });
    
    // Update button state, pricing, and flavour display
    this.updateButtonState();
    this.updateSubscriptionPricing();

    // Update flavour display when main section changes
    this.updateFlavourFromMainSection();
    
    // Update images from main gallery when syncing
    this.syncImagesFromMainForm();
  }

  /**
   * Updates images from a variant resource (from variant update events)
   * This is the most reliable way to get the correct image for products with multiple options
   * @param {any} variant - The variant resource from the event
   */
  updateImagesFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.featured_media) return;
    
    // @ts-ignore - Variant object from Shopify API
    const featuredMedia = variant.featured_media;
    const imageUrl = featuredMedia.preview_image?.src;
    
    if (!imageUrl) return;
    
    // Generate different sizes for different contexts
    const dropdownImageUrl = imageUrl + '&width=100';
    const productImageUrl = imageUrl + '&width=60';
    // @ts-ignore - Variant object from Shopify API
    const imageAlt = variant.title || variant.name || 'Product image';
    
    // Update ALL dropdown swatch images
    const imageContainers = this.querySelectorAll('.variant-option__selected-image');
    imageContainers.forEach((imageContainer) => {
      imageContainer.innerHTML = `<img src="${dropdownImageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
    });
    
    // Update ALL product image containers (both mobile-only and regular)
    const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
    productImageContainers.forEach((productImageContainer) => {
      const productImage = productImageContainer.querySelector('.sticky-product-image');
      if (productImage instanceof HTMLImageElement) {
        productImage.src = productImageUrl;
        productImage.srcset = productImageUrl;
        productImage.alt = imageAlt;
      }
    });
  }

  /**
   * Syncs images from the main product form/gallery
   * Used when sticky bar first shows or when syncing from main form
   */
  syncImagesFromMainForm() {
    // Try to get the currently displayed image from the main product gallery
    const mainGalleryImage = document.querySelector('media-gallery img.product__media-item, .product__media img, [data-product-media-viewer] img');
    
    if (mainGalleryImage instanceof HTMLImageElement && mainGalleryImage.src) {
      const imageUrl = mainGalleryImage.src.split('?')[0]; // Remove query params
      const imageAlt = mainGalleryImage.alt || 'Product image';
      
      // Generate different sizes for different contexts
      const dropdownImageUrl = imageUrl + '?width=100';
      const productImageUrl = imageUrl + '?width=60';
      
      // Update ALL dropdown swatch images
      const imageContainers = this.querySelectorAll('.variant-option__selected-image');
      imageContainers.forEach((imageContainer) => {
        imageContainer.innerHTML = `<img src="${dropdownImageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
      });
      
      // Update ALL product image containers (both mobile-only and regular)
      const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
      productImageContainers.forEach((productImageContainer) => {
        const productImage = productImageContainer.querySelector('.sticky-product-image');
        if (productImage instanceof HTMLImageElement) {
          productImage.src = productImageUrl;
          productImage.srcset = productImageUrl;
          productImage.alt = imageAlt;
        }
      });
    }
  }

  /**
   * Updates flavor text from a variant resource (from variant update events)
   * @param {any} variant - The variant resource from the event
   */
  updateFlavourTextFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.title) return;
    
    // Skip if it's the default variant
    // @ts-ignore - Variant object from Shopify API
    if (variant.title === 'Default Title') return;
    
    // Update ALL flavour labels in the sticky bar (both mobile and desktop)
    const flavourLabels = this.querySelectorAll('.flavour-label');
    
    // @ts-ignore - Variant object from Shopify API
    const variantTitle = variant.title;
    
    flavourLabels.forEach((flavourLabel) => {
      if (flavourLabel && variantTitle) {
        flavourLabel.textContent = variantTitle;
      }
    });
  }

  /**
   * Read allocation-based prices (cents) from the PDP price container or subscription widget.
   * @returns {{ subCents: number, compareCents: number } | null}
   */
  getAllocationPricesFromDom() {
    const sources = [
      document.querySelector('product-price [ref="priceContainer"]'),
      document.querySelector('[ref="priceContainer"]'),
      document.querySelector('[id^="subscription-variant--"]:not([style*="display: none"])'),
      document.querySelector('[id^="subscription-variant--"]'),
      this,
    ];

    for (const source of sources) {
      if (!source?.dataset?.subscriptionCents) continue;

      const subCents = parseInt(source.dataset.subscriptionCents, 10);
      if (isNaN(subCents)) continue;

      const compareParsed = parseInt(source.dataset.compareCents, 10);
      const compareCents = isNaN(compareParsed) ? subCents : compareParsed;

      return { subCents, compareCents };
    }

    return null;
  }

  /**
   * @param {number} compareCents
   * @param {number} subCents
   * @returns {number}
   */
  formatDiscountFromCents(compareCents, subCents) {
    if (!compareCents || compareCents <= subCents) return 0;
    return Math.round((((compareCents - subCents) / compareCents) * 100) * 10) / 10;
  }

  /**
   * @param {number} compareCents
   * @param {number} subCents
   * @returns {string}
   */
  formatDiscountLabel(compareCents, subCents) {
    const discount = this.formatDiscountFromCents(compareCents, subCents);
    if (discount) {
      return Number.isInteger(discount) ? String(discount) : discount.toFixed(1);
    }

    const metafieldDiscount = parseFloat(this.dataset.subscriptionDiscount || '0');
    if (metafieldDiscount > 0) {
      return Number.isInteger(metafieldDiscount) ? String(metafieldDiscount) : String(metafieldDiscount);
    }

    const priceContainer = document.querySelector('product-price [ref="priceContainer"]');
    const containerDiscount = parseFloat(priceContainer?.dataset?.subscriptionDiscount || '0');
    if (containerDiscount > 0) {
      return Number.isInteger(containerDiscount) ? String(containerDiscount) : String(containerDiscount);
    }

    return '';
  }

  /**
   * @param {string} key
   * @param {Record<string, string|number>} [replacements]
   * @param {string} [fallback]
   * @returns {string}
   */
  formatSubscriptionCopy(key, replacements = {}, fallback = '') {
    if (typeof window.Theme?.formatCopy === 'function') {
      return window.Theme.formatCopy(key, replacements, fallback);
    }

    const translations = window.Theme?.translations || {};
    let template = String(translations[key] || fallback).replaceAll('&amp;', '&');

    for (const [token, value] of Object.entries(replacements)) {
      template = template.split(token).join(String(value));
    }

    return template;
  }

  /**
   * @param {Element|null} element
   * @param {string} formattedPrice
   * @param {string} [labelKey]
   */
  setPriceText(element, formattedPrice, labelKey) {
    if (!element) return;

    const hiddenLabel = element.querySelector(':scope > .visually-hidden');
    if (hiddenLabel) {
      while (hiddenLabel.nextSibling) {
        hiddenLabel.nextSibling.remove();
      }
      if (formattedPrice) {
        hiddenLabel.insertAdjacentText('afterend', formattedPrice);
      }
      return;
    }

    if (labelKey && formattedPrice) {
      const label = this.formatSubscriptionCopy(labelKey, {}, '');
      if (label) {
        element.replaceChildren();
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = `${label}\u00a0`;
        element.append(span, formattedPrice);
        return;
      }
    }

    element.textContent = formattedPrice;
  }

  /**
   * @param {number} subCents
   * @param {number} compareCents
   */
  updateSubscriptionLabels(subCents, compareCents) {
    const hasSubZero = this.getAttribute('data-has-sub-zero') === 'true';
    const hasSubSave = this.getAttribute('data-has-sub-save') === 'true';
    const discountLabel = this.formatDiscountLabel(compareCents, subCents);

    const headerSpan = this.querySelector('label[for="sticky-subscription"] .subscription-option-header > span:last-child');
    const titleEl = this.querySelector('.subscription-title');

    if (hasSubZero) {
      if (headerSpan) headerSpan.textContent = this.formatSubscriptionCopy('subscribe', {}, 'SUBSCRIBE');
      if (titleEl) titleEl.hidden = true;
      return;
    }

    if (discountLabel) {
      const label = this.formatSubscriptionCopy(
        'subscribe_and_save_percent',
        { __PERCENT__: discountLabel },
        `SUBSCRIBE & SAVE ${discountLabel}%`
      );
      if (headerSpan) headerSpan.textContent = label;
      if (titleEl) {
        titleEl.hidden = false;
        titleEl.textContent = label;
      }
      return;
    }

    if (hasSubSave) {
      const label = this.formatSubscriptionCopy('subscribe_and_save', {}, 'SUBSCRIBE & SAVE');
      if (headerSpan) headerSpan.textContent = label;
      if (titleEl) {
        titleEl.hidden = false;
        titleEl.textContent = label;
      }
      return;
    }

    if (headerSpan) headerSpan.textContent = this.formatSubscriptionCopy('subscribe', {}, 'SUBSCRIBE');
    if (titleEl) titleEl.hidden = true;
  }

  /**
   * @param {number} compareCents
   * @param {number} subCents
   * @param {boolean} showWhenSelected
   */
  updateStickyComparePrice(compareCents, subCents, showWhenSelected = true) {
    const hasSubZero = this.getAttribute('data-has-sub-zero') === 'true';
    const compareEl = this.querySelector('.subscription-option--subscription .subscription-pricing .subscription-original-price');

    if (!compareEl) return;

    if (hasSubZero || compareCents <= subCents) {
      compareEl.hidden = true;
      this.setPriceText(compareEl, '', 'price_regular');
      return;
    }

    this.setPriceText(compareEl, this.formatMoney(compareCents), 'price_regular');
    compareEl.hidden = !showWhenSelected;
  }

  /**
   * Resolve subscription/compare cents from DOM widgets, data attributes, or variant price.
   * @param {number|null} variantPriceCents
   * @returns {{ subCents: number, compareCents: number }}
   */
  resolveStickyPrices(variantPriceCents) {
    const allocationPrices = this.getAllocationPricesFromDom();
    if (allocationPrices) {
      return allocationPrices;
    }

    const compareCents = variantPriceCents || parseInt(this.dataset.compareCents || '0', 10) || 0;
    let subCents = parseInt(this.dataset.subscriptionCents || '0', 10);

    const subscriptionWrapper = document.querySelector('[id^="subscription-variant--"]:not([style*="display: none"])')
      || document.querySelector('[id^="subscription-variant--"]');
    const widgetPriceElement = subscriptionWrapper?.querySelector('[data-widget-price-sub-price]');
    const widgetCents = parseInt(widgetPriceElement?.dataset?.cents || '', 10);

    if (!isNaN(widgetCents) && widgetCents > 0) {
      subCents = widgetCents;
    } else if (!subCents && compareCents) {
      subCents = compareCents;
    }

    const wrapperCompare = parseInt(subscriptionWrapper?.dataset?.compareCents || '', 10);
    return {
      subCents,
      compareCents: !isNaN(wrapperCompare) && wrapperCompare > 0 ? wrapperCompare : compareCents,
    };
  }

  /**
   * @param {number} subCents
   * @param {number} compareCents
   * @param {boolean} [showCompareWhenSelected=true]
   */
  applyStickySubscriptionPrices(subCents, compareCents, showCompareWhenSelected = true) {
    this.dataset.subscriptionCents = String(subCents);
    this.dataset.compareCents = String(compareCents);

    const subscriptionPriceElement = this.querySelector('.subscription-option--subscription .subscription-pricing .subscription-price');
    if (subscriptionPriceElement) {
      this.setPriceText(subscriptionPriceElement, this.formatMoney(subCents), 'price_sale');
    }

    this.updateStickyComparePrice(compareCents, subCents, showCompareWhenSelected);
    this.updateSubscriptionLabels(subCents, compareCents);
  }

  /**
   * Updates pricing from a variant resource (from variant update events)
   * This updates both one-time and subscription prices in the sticky bar
   * @param {any} variant - The variant resource from the event
   */
  updatePricingFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.price) return;
    
    // @ts-ignore - Variant object from Shopify API
    const currentVariantPrice = variant.price;
    const { subCents, compareCents } = this.resolveStickyPrices(currentVariantPrice);
    
    // Update ALL one-time price elements (in case there are multiple)
    const allOneTimePrices = this.querySelectorAll('.subscription-price.one-time-price');
    const newOnetimePrice = this.formatMoney(currentVariantPrice);
    
    allOneTimePrices.forEach((el) => {
      el.textContent = newOnetimePrice;
    });

    this.applyStickySubscriptionPrices(subCents, compareCents);
  }

  updateAllVariantImages() {
    // This method is now replaced by syncImagesFromMainForm
    // Keeping for backward compatibility
    this.syncImagesFromMainForm();
  }

  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateAllImageContainers(selectElement) {
    // Only update image for flavour option
    const optionName = selectElement.getAttribute('data-option-name');
    
    if (optionName && optionName.toLowerCase() === 'flavour') {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (selectedOption) {
        const optionValue = selectedOption.value;
        const imageUrl = selectedOption.getAttribute('data-variant-image-url');
        const imageAlt = selectedOption.getAttribute('data-variant-image-alt') || optionValue;
        const variantTitle = selectedOption.getAttribute('data-variant-title') || optionValue;
        
        if (imageUrl) {
          // Update ALL image containers in the sticky bar (just like flavour labels)
          const imageContainers = this.querySelectorAll('.variant-option__selected-image');
          
          imageContainers.forEach((imageContainer) => {
            imageContainer.innerHTML = `<img src="${imageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
          });

          // Update ALL product image containers (both mobile-only and regular)
          const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
          
          productImageContainers.forEach((productImageContainer) => {
            const productImage = productImageContainer.querySelector('.sticky-product-image');
            if (productImage instanceof HTMLImageElement) {
              productImage.src = imageUrl;
              productImage.srcset = imageUrl;
              productImage.alt = variantTitle;
            }
          });
        } else {
          console.warn('❌ StickyAddToCart: No image URL found for variant:', optionValue);
          
          // Update ALL image containers with placeholder
          const imageContainers = this.querySelectorAll('.variant-option__selected-image');
          imageContainers.forEach((imageContainer) => {
            imageContainer.innerHTML = '<div class="sticky-variant-placeholder"></div>';
          });
        }
      }
    }
  }

  // Keep the old method for backward compatibility but rename it
  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateStickyVariantImage(selectElement) {
    // This method now just calls the new method
    this.updateAllImageContainers(selectElement);
  }

  setupSubscriptionListeners() {
    // Listen for subscription radio button changes
    const subscriptionRadios = this.querySelectorAll('input[name="sticky_purchase_type"]');
    subscriptionRadios.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        if(!(event.target instanceof HTMLInputElement)) return;
        this.handleSubscriptionChange(event.target);
      });
    });

    // Simple: Don't sync from main on load, let Liquid default handle it
  }

  setupMainSubscriptionListeners() {
    // Listen for subscription radio button changes in the MAIN form
    const mainSubscriptionRadios = document.querySelectorAll('input[name="purchase_type"]');

    mainSubscriptionRadios.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        if(!(event.target instanceof HTMLInputElement)) return;
        this.syncSubscriptionFromMain(event.target);
      });
    });
  }

  syncSubscriptionFromMain(mainRadio) {
    const isSubscription = mainRadio.value === 'autodeliver';

    // Update sticky radio buttons
    const stickyOneTimeRadio = this.querySelector('input[name="sticky_purchase_type"][value="onetime"]');
    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"][value="subscription"]');

    if (isSubscription && stickySubscriptionRadio) {
      stickySubscriptionRadio.checked = true;
    } else if (!isSubscription && stickyOneTimeRadio) {
      stickyOneTimeRadio.checked = true;
    }

    // Prices are updated via the 'subscription:price-update' event from main form
    // No need to call updateSubscriptionPricing() here
  }

  /**
   * 
   * @param {HTMLInputElement} radio 
   */
  handleSubscriptionChange(radio) {
    if(!(radio instanceof HTMLInputElement)) return;
    let isSubscription = radio.value === 'subscription';
    
    const mainPurchaseTypeRadio = document.querySelector('input#rc_purchase_type_onetime');

    if (!mainPurchaseTypeRadio) {
      isSubscription = false;
    } 
    // Sync with main form (this will trigger the main form's change event, which will dispatch price update event)
    this.syncSubscriptionToMain(isSubscription);
    
    // Prices are updated via the 'subscription:price-update' event from main form
    // No need to call updateSubscriptionPricing() here
  }

  /**
   * 
   * @param {boolean} isSubscription 
   */
  syncSubscriptionToMain(isSubscription) {
    // Check if the new customizable widget buttons exist in the currently visible container
    const visibleContainer = document.querySelector('[id^="subscription-variant--"]:not([style*="display: none"])');
    const newWidgetSubscriptionBtn = visibleContainer?.querySelector('#rc_purchase_type_autodeliver');
    const newWidgetOneTimeBtn = visibleContainer?.querySelector('#rc_purchase_type_onetime');
    
    // If new widget buttons exist, use click to trigger all widget logic
    if (newWidgetSubscriptionBtn && newWidgetOneTimeBtn) {
      if (isSubscription) {
        newWidgetSubscriptionBtn.click();
      } else {
        newWidgetOneTimeBtn.click();
      }
      return;
    }
    
    // Otherwise, use the old widget logic
    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]');
    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]');
    
    if (isSubscription && mainSubscriptionRadio instanceof HTMLInputElement) {
      mainSubscriptionRadio.checked = true;
      // Dispatch change event directly - don't use .click() as it triggers onclick handlers
      mainSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!isSubscription && mainOneTimeRadio instanceof HTMLInputElement) {
      mainOneTimeRadio.checked = true;
      // Dispatch change event directly - don't use .click() as it triggers onclick handlers
      // The onclick="deselectSubscription(event)" on the parent div would fail if called
      mainOneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  

  /**
   * Handle subscription price update event from main form
   * @param {Object} detail - Event detail with price information
   */
  handleSubscriptionPriceUpdate(detail) {
    const { isSubscription, subscriptionPrice, oneTimePrice, displayPrice } = detail;
    
    // Convert to cents (prices from event are in dollars)
    const subscriptionPriceCents = Math.round(subscriptionPrice * 100);
    const oneTimePriceCents = Math.round(oneTimePrice * 100);
    
    // For "Try once" option, we need to get the one-time widget price
    // When subscription is selected, displayPrice is the subscription price, so we need to get the one-time price differently
    let tryOncePriceCents = oneTimePriceCents; // Default to regular one-time price
    
    if (!isSubscription) {
      // When one-time is selected, displayPrice is the one-time widget price (may include discounts)
      const displayPriceCents = displayPrice ? Math.round(displayPrice * 100) : oneTimePriceCents;
      tryOncePriceCents = displayPriceCents;
    } else {
      // When subscription is selected, we need to get the one-time widget price from the DOM
      // because displayPrice is the subscription price in this case
      const oneTimeSelector = document.querySelector('[data-one-time-selector]');
      if (oneTimeSelector) {
        const oneTimeWidgetPriceElement = oneTimeSelector.querySelector('[data-widget-price-sub-price]');
        if (oneTimeWidgetPriceElement && oneTimeWidgetPriceElement.textContent.trim()) {
          const widgetPriceText = oneTimeWidgetPriceElement.textContent.trim();
          const parsedPrice = parseFloat(widgetPriceText.replace(/[^0-9.]/g, ''));
          if (!isNaN(parsedPrice) && parsedPrice > 0) {
            tryOncePriceCents = Math.round(parsedPrice * 100);
          }
        }
      }
    }
    
    const allocationPrices = this.getAllocationPricesFromDom();
    const stampedCompare = parseInt(this.dataset.compareCents || '0', 10);
    const finalSubCents = allocationPrices?.subCents ?? subscriptionPriceCents;
    const finalCompareCents = allocationPrices?.compareCents
      ?? (stampedCompare > 0 ? stampedCompare : oneTimePriceCents);
    
    // Update one-time price display ("Try once" option)
    const oneTimePriceElement = this.querySelector('.subscription-price.one-time-price');
    if (oneTimePriceElement) {
      const formattedOneTime = this.formatMoney(tryOncePriceCents);
      oneTimePriceElement.textContent = formattedOneTime;
    }

    this.applyStickySubscriptionPrices(finalSubCents, finalCompareCents, isSubscription);
  }

  updateSubscriptionPricing() {
    // Get current variant price from the subscription widget's one-time price element
    // IMPORTANT: Both subscription and one-time use [data-widget-price-sub-price]
    // But they're in different contexts:
    // - Subscription: [data-widget-price-sub-price] inside wrapper/subscription section
    // - One-time: [data-widget-price-sub-price] inside [data-one-time-selector]
    
    let currentVariantPrice = null;
    let subscriptionPrice = null;
    
    // First try: look for [data-widget-price-onetime] (if it exists in Liquid)
    let mainOneTimePriceElement = document.querySelector('[data-widget-price-onetime]');
    
    // Second try: look for [data-widget-price-sub-price] inside [data-one-time-selector]
    // This is the actual element that gets populated for one-time purchase
    if (!mainOneTimePriceElement) {
      const oneTimeSelector = document.querySelector('[data-one-time-selector]');
      if (oneTimeSelector) {
        mainOneTimePriceElement = oneTimeSelector.querySelector('[data-widget-price-sub-price]');
      }
    }
    
    if (mainOneTimePriceElement && mainOneTimePriceElement.textContent) {
      // Parse the price from the text content (e.g., "$95.00" -> 9500)
      const priceText = mainOneTimePriceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        const priceNumber = parseFloat(priceMatch[0].replace(/,/g, ''));
        currentVariantPrice = Math.round(priceNumber * 100);
      }
    }

    // Get subscription price from main section (if subscription is available)
    // Look for [data-widget-price-sub-price] but NOT inside [data-one-time-selector]
    const subscriptionWrapper = document.querySelector('[data-subscription-selector], [id^="subscription-variant--"]');
    let mainSubscriptionPriceElement = null;
    
    if (subscriptionWrapper) {
      mainSubscriptionPriceElement = subscriptionWrapper.querySelector('[data-widget-price-sub-price]');
    }
    
    // Fallback: search for subscription price element (not in one-time selector)
    if (!mainSubscriptionPriceElement) {
      const allSubPriceElements = document.querySelectorAll('[data-widget-price-sub-price]');
      for (const element of allSubPriceElements) {
        // Skip if it's inside one-time selector
        if (!element.closest('[data-one-time-selector]')) {
          mainSubscriptionPriceElement = element;
          break;
        }
      }
    }
    
    if (mainSubscriptionPriceElement && mainSubscriptionPriceElement.textContent) {
      const priceText = mainSubscriptionPriceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        const priceNumber = parseFloat(priceMatch[0].replace(/,/g, ''));
        subscriptionPrice = Math.round(priceNumber * 100);
      }
    }
    
    // Fallback to sticky dropdown price ONLY if BOTH prices are missing
    if (!currentVariantPrice && !subscriptionPrice) {
      currentVariantPrice = this.getCurrentVariantPrice();
    }
    
    if (!currentVariantPrice) return;

    const hasSubZero = this.getAttribute('data-has-sub-zero') === 'true';
    const hasSubSave = this.getAttribute('data-has-sub-save') === 'true';
    
    // Initialize subscriptionPrice if not already set from main section
    if (subscriptionPrice === null) {
      subscriptionPrice = currentVariantPrice;
    }
    
    let sellingPlansSelect = document.querySelector('[name="selling_plan"]');
    if (!sellingPlansSelect) {
      const productForm = document.querySelector('form[action^="/cart"]');
      sellingPlansSelect = productForm?.querySelector('[name="selling_plan"]');
    }
    if (!sellingPlansSelect) {
      const subscriptionWrapper = document.querySelector('[id^="subscription-variant--"]');
      sellingPlansSelect = subscriptionWrapper?.querySelector('[name="selling_plan"]');
    }

    const selectedPlan = sellingPlansSelect?.querySelector('option:checked');
    const rechargePrice = selectedPlan?.dataset.price;
    const allocationPrices = this.getAllocationPricesFromDom();
    let compareCents = allocationPrices?.compareCents
      || parseInt(this.dataset.compareCents || '0', 10)
      || currentVariantPrice;

    if (hasSubZero) {
      subscriptionPrice = currentVariantPrice;
      compareCents = currentVariantPrice;
    } else if (hasSubSave && rechargePrice) {
      const rechargePriceValue = parseFloat(rechargePrice.replace(/[^0-9.]/g, ''));
      subscriptionPrice = Math.round(rechargePriceValue * 100);
    } else if (hasSubSave && !rechargePrice) {
      subscriptionPrice = currentVariantPrice;
    } else if (allocationPrices) {
      subscriptionPrice = allocationPrices.subCents;
      compareCents = allocationPrices.compareCents;
    }

    const oneTimePriceElement = this.querySelector('.subscription-price.one-time-price');
    if (oneTimePriceElement) {
      oneTimePriceElement.textContent = this.formatMoney(currentVariantPrice);
    }

    this.applyStickySubscriptionPrices(subscriptionPrice, compareCents);
  }

  /**
   * 
   * @param { {featured_media: {preview_image: {src: string, alt: string} }, title: string} } variant 
   */
  updateStickyProductImage(variant) {
    const productImage = this.querySelector('.sticky-product-image');
    if (productImage instanceof HTMLImageElement && variant.featured_media && variant.featured_media.preview_image) {
      productImage.src = variant.featured_media.preview_image.src;
      productImage.alt = variant.title;
    }
  }

  getCurrentSubscriptionState() {
    const mainPurchaseTypeRadio = document.querySelector('input#rc_purchase_type_onetime');
    if (!mainPurchaseTypeRadio) return false;

    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]:checked');
    if (mainSubscriptionRadio) return true;

    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"][value="subscription"]:checked');
    if (stickySubscriptionRadio) return true;

    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]:checked');
    const stickyOneTimeRadio = this.querySelector('input[name="sticky_purchase_type"][value="onetime"]:checked');
    if (mainOneTimeRadio || stickyOneTimeRadio) {
      return false;
    }
    return false;
  }

  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateSelectedFlavourFromSticky(selectElement) {
    // Only update flavour for flavour option changes
    const optionName = selectElement.getAttribute('data-option-name');
    if (optionName && optionName.toLowerCase() === 'flavour') {
      const selectedValue = selectElement.value;
      // Update ALL flavour labels in the sticky bar
      const flavourLabels = this.querySelectorAll('.flavour-label');
      
      flavourLabels.forEach((flavourLabel) => {
        if (flavourLabel && selectedValue) {
          flavourLabel.textContent = selectedValue;
        }
      });
      
      if (flavourLabels.length > 0 && selectedValue) {
      }
    }
  }

  updateFlavourFromMainSection() {
    // Find the checked flavour radio button in main section
    const mainFlavourRadio = document.querySelector('input[type="radio"][name*="Flavour"]:checked');
    // Update ALL flavour labels in the sticky bar
    const flavourLabels = this.querySelectorAll('.flavour-label');
    
    if (mainFlavourRadio instanceof HTMLInputElement && flavourLabels.length > 0) {
      const selectedValue = mainFlavourRadio.value;
      if (selectedValue) {
        flavourLabels.forEach((flavourLabel) => {
          flavourLabel.textContent = selectedValue;
        });
      }
    }
  }

  getCurrentVariantId() {
    // Get current variant ID from the main form
    const variantIdInput = document.querySelector('input[name="id"]');
    return variantIdInput instanceof HTMLInputElement ? parseInt(variantIdInput.value) : null;
  }

  getCurrentVariantPrice() {
    // Look for selected options in all select dropdowns
    const selectElements = this.querySelectorAll('.variant-option__select');
    
    for (const selectElement of selectElements) {
      if (selectElement instanceof HTMLSelectElement) {
        const selectedOption = selectElement.options[selectElement.selectedIndex];
        if (selectedOption) {
          const variantPrice = selectedOption.getAttribute('data-variant-price');
          if (variantPrice) {
            return parseInt(variantPrice);
          }
        }
      }
    }
    
    return null;
  }

  /**
   * 
   * @param {number} cents 
   */
  formatMoney(cents) {
    // Simple money formatting - you can enhance this later
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * 
   * @param {string} optionName 
   * @param {string} optionValue 
   */
  syncMainVariantSelection(optionName, optionValue) {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      console.warn('❌ StickyAddToCart: Variant picker not found');
      this.updateButtonState();
      return;
    }
    
    // Strategy 1: Try radio buttons with theme-specific naming pattern (most reliable for this theme)
    const blockId = variantPicker.getAttribute('data-block-id');
    const productId = variantPicker.getAttribute('data-product-id');
    
    if (blockId && productId) {
      // Try the theme-specific radio button naming pattern
      const radioName = `${optionName}-${blockId}-${productId}`;
      const radioButton = variantPicker.querySelector(`input[type="radio"][name="${radioName}"][value="${optionValue}"]`);
      
      if (radioButton instanceof HTMLInputElement) {
        if (!radioButton.checked) {
          // Use variant-picker's updateSelectedOption method to properly update
          if ('updateSelectedOption' in variantPicker) {
            try {
              // @ts-ignore - Custom method on variant-picker web component
              variantPicker.updateSelectedOption(radioButton);
            } catch (error) {
              // Fallback: manually check and trigger change
              radioButton.checked = true;
            }
          } else {
            radioButton.checked = true;
          }
          
          // Trigger change event on the radio button itself (not the variant-picker)
          // This will trigger variant-picker's variantChanged() method
          radioButton.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateButtonState();
          this.clickNewWidgetPurchaseButton();
          return;
        } else {
          // Already checked, just update button state
          this.updateButtonState();
          return;
        }
      }
    }
    
    // Strategy 2: Try select elements with standard Shopify naming
    const mainOptionSelector = variantPicker.querySelector(`select[name="options[${optionName}]"]`);
    
    if (mainOptionSelector instanceof HTMLSelectElement) {
      if (mainOptionSelector.value !== optionValue) {
        // Use variant-picker's updateSelectedOption method if available
        if ('updateSelectedOption' in variantPicker) {
          try {
            // Find the option element and update the select
            const targetOption = Array.from(mainOptionSelector.options).find(
              opt => opt.value === optionValue
            );
            if (targetOption) {
              mainOptionSelector.value = optionValue;
              // @ts-ignore - Custom method on variant-picker web component
              variantPicker.updateSelectedOption(mainOptionSelector);
            }
          } catch (error) {
            // Fallback: manually set value
            mainOptionSelector.value = optionValue;
          }
        } else {
          mainOptionSelector.value = optionValue;
        }
        
        // Trigger change event on the select element itself
        mainOptionSelector.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateButtonState();
        this.clickNewWidgetPurchaseButton();
        return;
      } else {
        this.updateButtonState();
        return;
      }
    }
    
    // Strategy 3: Try standard radio button naming
    const standardRadioButton = variantPicker.querySelector(`input[type="radio"][name="options[${optionName}]"][value="${optionValue}"]`);
    
    if (standardRadioButton instanceof HTMLInputElement) {
      if (!standardRadioButton.checked) {
        // Use variant-picker's updateSelectedOption method if available
        if ('updateSelectedOption' in variantPicker) {
          try {
            // @ts-ignore - Custom method on variant-picker web component
            variantPicker.updateSelectedOption(standardRadioButton);
          } catch (error) {
            standardRadioButton.checked = true;
          }
        } else {
          standardRadioButton.checked = true;
        }
        
        standardRadioButton.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateButtonState();
        this.clickNewWidgetPurchaseButton();
        return;
      } else {
        this.updateButtonState();
        return;
      }
    }
    
    // Strategy 4: Try to find any radio button with the value (wildcard search within variant-picker)
    const allRadioButtons = variantPicker.querySelectorAll(`input[type="radio"][value="${optionValue}"]`);
    
    for (const radio of allRadioButtons) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const radioName = radio.name;
      
      // Check if this radio button name contains the option name (case-insensitive)
      if (radioName.toLowerCase().includes(optionName.toLowerCase())) {
        if (!radio.checked) {
          // Use variant-picker's updateSelectedOption method if available
          if ('updateSelectedOption' in variantPicker) {
            try {
              // @ts-ignore - Custom method on variant-picker web component
              variantPicker.updateSelectedOption(radio);
            } catch (error) {
              radio.checked = true;
            }
          } else {
            radio.checked = true;
          }
          
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateButtonState();
          this.clickNewWidgetPurchaseButton();
          return;
        } else {
          this.updateButtonState();
          return;
        }
      }
    }
    
    console.warn('❌ StickyAddToCart: Could not sync variant:', optionName, optionValue);
    this.updateButtonState();
    
    // After variant change, click the currently selected subscription option in the new widget if it exists
    this.clickNewWidgetPurchaseButton();
  }
  
  clickNewWidgetPurchaseButton() {
    // Wait a bit for variant change to complete
    setTimeout(() => {
      // Find the currently visible subscription widget container
      const visibleContainer = document.querySelector('[id^="subscription-variant--"]:not([style*="display: none"])');
      if (!visibleContainer) return;
      
      // Check if new widget buttons exist
      const newWidgetSubscriptionBtn = visibleContainer.querySelector('#rc_purchase_type_autodeliver');
      const newWidgetOneTimeBtn = visibleContainer.querySelector('#rc_purchase_type_onetime');
      
      if (!newWidgetSubscriptionBtn || !newWidgetOneTimeBtn) return;
      
      // Find which one is currently checked and click it to refresh prices
      const checkedBtn = visibleContainer.querySelector('input[name="purchase_type"]:checked');
      if (checkedBtn) {
        checkedBtn.click();
      }
    }, 200);
  }

  syncStickySubscriptionToMainForm() {
    // Get sticky subscription selection
    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"]:checked');
    
    if (!(stickySubscriptionRadio instanceof HTMLInputElement)) return;

    const isSubscription = stickySubscriptionRadio.value === 'subscription';
    
    // Update main form subscription selection
    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]');
    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]');
    
    if (isSubscription && mainSubscriptionRadio instanceof HTMLInputElement) {
      mainSubscriptionRadio.checked = true;
      mainSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!isSubscription && mainOneTimeRadio instanceof HTMLInputElement) {
      mainOneTimeRadio.checked = true;
      mainOneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  triggerVariantUpdate() {
    // The variant-picker handles updates automatically through its change event listener
    // We don't need to manually trigger updates since updateSelectedOption() + change event does it all
    const variantPicker = document.querySelector('variant-picker');
    
    if (variantPicker) {
      // The variant picker automatically handles updates when we call updateSelectedOption() and dispatch change event
      // Just dispatch additional events for any other components that might be listening
      variantPicker.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
      variantPicker.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
    }
    
    // Fallback: Force update variant-dependent elements by triggering events
    const productForm = document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
      // Trigger form change event
      productForm.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Look for variant picker elements and trigger their update
      if (variantPicker) {
        variantPicker.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
        variantPicker.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
      }
      
      // Look for product form elements and trigger their update
      const productFormElement = document.querySelector('product-form, [data-product-form]');
      
      if (productFormElement) {
        productFormElement.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
      }
      
      // Trigger a global variant change event for any other listeners
      document.dispatchEvent(new CustomEvent('product:variant:change', { 
        bubbles: true,
        detail: { source: 'sticky-add-to-cart' }
      }));
    }
  }

  updateButtonState() {
    // Check if any option is unavailable or has sold-out metafield
    // IMPORTANT: This method should ONLY disable the button if sold out
    // It should NOT enable the button - that's handled by variant update events
    // This prevents the button from being re-enabled incorrectly when syncVariantSelection() runs
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    const optionSelectors = this.querySelectorAll('select[name^="options["]');
    let hasUnavailableOption = false;
    let hasSoldOutMetafield = false;
    
    optionSelectors.forEach((selector) => {
      if (!(selector instanceof HTMLSelectElement)) return;
      const selectedOption = selector.options[selector.selectedIndex];
      
      // Check standard availability
      if (selectedOption && (selectedOption.getAttribute('data-variant-available') ?? '').includes('false')) {
        hasUnavailableOption = true;
      }
      
      // Check sold-out metafield
      if (selectedOption && (selectedOption.getAttribute('data-variant-sold-out-metafield') ?? '').includes('true')) {
        hasSoldOutMetafield = true;
      }
    });
    
    const isSoldOut = hasUnavailableOption || hasSoldOutMetafield;
    
    // Only disable if sold out - don't enable here, let variant update events handle enabling
    // This prevents the button from being incorrectly re-enabled when syncVariantSelection() runs
    if (isSoldOut) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.setAttribute('aria-label', this.formatSubscriptionCopy('sold_out', {}, 'Sold out'));
      this.addToCartButton.dataset.available = 'false';
      
      const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
      if (buttonText) {
        buttonText.textContent = this.formatSubscriptionCopy('sold_out', {}, 'Sold Out');
      }
    } else {
      // If not sold out, update the label and text, but don't change disabled state
      // The variant update events will handle enabling/disabling based on form sync status
      this.addToCartButton.setAttribute(
        'aria-label',
        this.formatSubscriptionCopy('add_to_cart', {}, 'Add to cart')
      );
      const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
      if (buttonText) {
        buttonText.textContent = this.formatSubscriptionCopy('add_to_cart', {}, 'Add to Cart');
      }
    }
  }

  forceRefreshVariants() {
    // Fallback method to force refresh variant-dependent elements
    
    // Trigger a page refresh as last resort (only if absolutely necessary)
    const shouldRefresh = confirm('Variant synchronization failed. Refresh the page to fix this issue?');
    if (shouldRefresh) {
      window.location.reload();
    }
  }

  /**
   * 
   * @param {string} message 
   */
  announceToScreenReader(message) {
    // Create a live region for screen reader announcements
    let liveRegion = document.getElementById('sticky-add-to-cart-announcement');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'sticky-add-to-cart-announcement';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    // Clear the message after announcement
    setTimeout(() => {
      if(liveRegion instanceof HTMLDivElement) {
        liveRegion.textContent = '';
      }
    }, 1000);
  }

  /**
   * Shop now button mobile handle
  */
  // shopNowHandle() {
  //   this.shopNowButton?.addEventListener('click', () => {
  //     if(this.shopNowContainer?.hasAttribute('data-mobile-expanded')) {
  //       this.shopNowContainer?.removeAttribute('data-mobile-expanded');
  //     } else {
  //       this.shopNowContainer?.setAttribute('data-mobile-expanded', "true");
  //     }
  //   })

  //   window.addEventListener('resize', () => {
  //     this.shopNowContainer?.removeAttribute('data-mobile-expanded');
  //   })
  // }

  shopNowHandle() {
  const mobileShopNowEl = this.querySelector('.sticky-add-to-cart__mobile-shop-now');

  this.shopNowButton?.addEventListener('click', () => {
    if (this.shopNowContainer?.hasAttribute('data-mobile-expanded')) {
      this.shopNowContainer?.removeAttribute('data-mobile-expanded');
      if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "flex";
    } else {
      this.shopNowContainer?.setAttribute('data-mobile-expanded', "true");
      if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "none";
    }
  });

  window.addEventListener('resize', () => {
    this.shopNowContainer?.removeAttribute('data-mobile-expanded');
    if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "flex";
  });
}

  optionSelectorHandle() {
    let syncing = false; // guard flag

    /**
     * 
     * @param {HTMLSelectElement} source 
     * @param {Element[]} targets 
     * @returns 
     */
    const syncSelects = (source, targets) => {
      if (syncing) return; // prevent recursive loop
      syncing = true;

      targets.forEach(target => {
        if (!(target instanceof HTMLSelectElement)) return;
        if (source.name.toLowerCase() === target.name.toLowerCase() && source !== target) {
          target.value = source.value;
          // only dispatch if you REALLY need downstream listeners
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      syncing = false;
    };

    if (!(this.selectOptionSelectors instanceof NodeList)) return;
    Array.from(this.selectOptionSelectors).forEach((op) => {
      if (!(op instanceof HTMLSelectElement)) return;
      op.addEventListener('change', () => {
        if (!(this.thisOptionSelectors instanceof NodeList)) return;
        syncSelects(op, Array.from(this.thisOptionSelectors));
      });
    });

    if (!(this.thisOptionSelectors instanceof NodeList)) return;
    Array.from(this.thisOptionSelectors).forEach(op => {
      if (!(op instanceof HTMLSelectElement)) return;
      op.addEventListener('change', () => {
        syncSelects(op, Array.from(this.selectOptionSelectors));
      });
    });
  }


  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // Remove body padding
    document.body.style.paddingBottom = '';
  }
}

if(!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}